import { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Minimize2 } from 'lucide-react';
import { GrammarOverlay } from './GrammarOverlay';
import { DefinitionPopup } from './DefinitionPopup';
import { getDefinition, type Definition } from '@/lib/dictionary';
import { getSpellingSuggestions } from '@/lib/grammar';
import { useAppStore } from '@/lib/store';
import { useTypewriterAudio } from '@/hooks/useTypewriterAudio';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Typography from '@tiptap/extension-typography';

interface TypewriterModeProps {
    content: string;
    handleInput: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    handleKeyDown: (e: React.KeyboardEvent) => void;
    setEditorMode: (mode: 'standard' | 'typewriter' | 'book') => void;
    isDark: boolean;
    setTheme: (theme: 'light' | 'dark' | 'system') => void;
    themeColors: any;
    wordCount: number;
    charCount: number;
    lastSaved: Date | null;
    grammarIssues?: any[];
    isMobile?: boolean;
    setShowGrammarPanel?: (show: boolean) => void;
}

export function TypewriterMode({
    content,
    handleInput,
    handleKeyDown,
    setEditorMode,
    isDark,
    setTheme,
    themeColors,
    wordCount,
    charCount,
    lastSaved,
    grammarIssues,
    isMobile = false,
    setShowGrammarPanel
}: TypewriterModeProps) {
    const editorRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const { settings } = useAppStore();
    const { playSound } = useTypewriterAudio();

    // Definition popup state
    const [selectedDefinition, setSelectedDefinition] = useState<Definition | null>(null);
    const [selectedWord, setSelectedWord] = useState<string>('');
    const [selectedRange, setSelectedRange] = useState<{ start: number; end: number } | null>(null);
    const [spellingSuggestions, setSpellingSuggestions] = useState<string[]>([]);
    const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            ...(settings.smartTypographyEnabled ? [Typography] : [])
        ],
        content: content,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            const e = { target: { value: html } } as React.ChangeEvent<HTMLTextAreaElement>;
            handleInput(e);
        },
        onTransaction: ({ transaction }) => {
            if (settings.typewriterSoundsEnabled) {
                const lastStep = transaction.steps[transaction.steps.length - 1];
                if (lastStep) {
                    const stepJSON = lastStep.toJSON();
                    if (stepJSON.stepType === 'replace') {
                        const insertedText = stepJSON.slice?.content?.[0]?.text || '';
                        const isDeletion = !insertedText && stepJSON.from !== stepJSON.to;

                        if (isDeletion) {
                            playSound('delete');
                        } else if (insertedText === ' ') {
                            playSound('space');
                        } else if (insertedText.includes('\n')) {
                            playSound('enter');
                            if (settings.soundProfile === 'typewriter') {
                                setTimeout(() => playSound('ding'), 150);
                            }
                        } else if (insertedText.length > 0) {
                            playSound('key');
                        }
                    }
                }
            }
        },
        editorProps: {
            attributes: {
                class: 'w-full outline-none text-center bg-transparent prose prose-neutral dark:prose-invert max-w-none relative z-10 p-0',
                spellcheck: 'false',
            }
        }
    });

    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content);
        }
    }, [content, editor]);

    // Sync scroll between editor and overlay
    useEffect(() => {
        if (editorRef.current && overlayRef.current) {
            overlayRef.current.scrollTop = editorRef.current.scrollTop;
        }
    }, [editor]);

    useEffect(() => {
        if (editor) {
            editor.commands.focus();
        }
    }, [editor]);

    const handleDoubleClick = async (e: React.MouseEvent) => {
        if (!editor) return;

        // Let ProseMirror handle the native double-click selection first
        setTimeout(async () => {
            const { from, to, empty } = editor.state.selection;
            if (empty) return;

            const text = editor.state.doc.textBetween(from, to, ' ');
            const word = text.trim();

            if (word && word.length > 1) {
                setSelectedWord(word);
                setSelectedRange({ start: from, end: to });
                setPopupPosition({ x: e.clientX, y: e.clientY });

                const [def, sugs] = await Promise.all([
                    getDefinition(word),
                    getSpellingSuggestions(word)
                ]);

                setSelectedDefinition(def);
                setSpellingSuggestions(sugs);
            }
        }, 50);
    };

    const handleReplace = (newWord: string) => {
        if (!selectedRange || !editor) return;
        editor.chain().focus().insertContentAt({ from: selectedRange.start, to: selectedRange.end }, newWord).run();
        setSelectedRange(null);
    };

    if (isMobile) {
        return (
            <div
                className="h-full flex flex-col items-center justify-center px-4"
                style={{ backgroundColor: themeColors.bg }}
            >
                <EditorContent
                    editor={editor}
                    onKeyDown={handleKeyDown}
                    onDoubleClick={handleDoubleClick as any}
                    className="w-full h-full outline-none bg-transparent text-center py-8"
                    style={{ color: themeColors.text, fontSize: '1.1rem', lineHeight: '2', letterSpacing: '0.01em' }}
                />
            </div>
        );
    }

    return (
        <div
            className="fixed inset-0 z-50 flex flex-col"
            style={{ backgroundColor: themeColors.bg }}
            onClick={() => {
                editor?.commands.focus();
            }}
        >
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
                <button
                    onClick={() => setTheme(isDark ? 'light' : 'dark')}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: themeColors.textMuted, backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(90, 122, 122, 0.1)' }}
                >
                    {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>

                <button
                    onClick={() => setEditorMode('standard')}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: themeColors.textMuted, backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(90, 122, 122, 0.1)' }}
                >
                    <Minimize2 className="w-5 h-5" />
                </button>
            </div>

            <div className="flex-1 flex items-center justify-center px-8">
                <div className="relative tiptap-typewriter w-full max-w-3xl flex flex-col">
                    <div className="relative">
                        <EditorContent
                            editor={editor}
                            onKeyDown={handleKeyDown}
                            onDoubleClick={handleDoubleClick as any}
                            className="w-full outline-none text-center bg-transparent relative z-10 p-0 editor-richtext"
                            style={{ color: themeColors.text, fontSize: '1.25rem', lineHeight: '2.2', letterSpacing: '0.02em', minHeight: '60vh' }}
                        />

                        <div ref={overlayRef} className="absolute inset-0 pointer-events-none z-20">
                            <GrammarOverlay
                                text={editor ? editor.getText() : content}
                                issues={grammarIssues || []}
                                style={{
                                    fontSize: '1.25rem',
                                    lineHeight: '2.2',
                                    letterSpacing: '0.02em',
                                    textAlign: 'center',
                                    width: '100%',
                                    marginTop: '0.86em',
                                }}
                            />
                        </div>
                    </div>

                    <style>{`
                        .editor-richtext h1 {
                            font-size: 2.5rem;
                            font-weight: 700;
                            margin-top: 1.5em;
                            margin-bottom: 0.5em;
                        }
                        .editor-richtext h2 {
                            font-size: 2rem;
                            font-weight: 600;
                            margin-top: 1.2em;
                            margin-bottom: 0.4em;
                        }
                        .editor-richtext blockquote {
                            border-left: 4px solid var(--sage);
                            padding-left: 1.5rem;
                            font-style: italic;
                            margin: 1.5rem auto;
                            max-width: 80%;
                            color: var(--text-muted);
                            text-align: left;
                        }
                        .tiptap-typewriter .ProseMirror p {
                            margin-top: 0.8em;
                            margin-bottom: 0.8em;
                        }
                    `}</style>
                </div>
            </div>

            <div className="px-8 py-4 flex items-center justify-center gap-6 text-sm" style={{ color: themeColors.textMuted }}>
                <span>{wordCount.toLocaleString()} words</span>
                {grammarIssues && grammarIssues.length > 0 && (
                    <button
                        className="flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-black cursor-pointer transition-all duration-300 border-2 animate-rainbow-sweep group relative"
                        style={{
                            color: '#854d0e',
                            borderColor: '#d97706',
                            backgroundColor: 'transparent'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (setShowGrammarPanel) {
                                setShowGrammarPanel(true);
                            }
                        }}
                    >
                        <div className="relative">
                            <div className="w-2 h-2 rounded-full bg-amber-600" />
                            <div className="absolute -top-3 -right-3 w-4.5 h-4.5 rounded-full bg-red-600 text-white flex items-center justify-center text-[9px] font-black border-2 border-white shadow-md animate-bounce">
                                {grammarIssues.length}
                            </div>
                        </div>
                        <span className="tracking-tight">FIXES SUGGESTED</span>
                    </button>
                )}
                <span>{charCount.toLocaleString()} characters</span>
                {lastSaved && (
                    <span className="flex items-center gap-1">
                        Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                )}
            </div>

            {selectedWord && (
                <DefinitionPopup
                    word={selectedWord}
                    definition={selectedDefinition}
                    suggestions={spellingSuggestions}
                    position={popupPosition}
                    onClose={() => {
                        setSelectedWord('');
                        setSelectedDefinition(null);
                        setSelectedRange(null);
                        setSpellingSuggestions([]);
                    }}
                    onReplace={handleReplace}
                    themeColors={themeColors}
                />
            )}
        </div>
    );
}
